import React, { useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, FormField, LoadingModule, Text,
} from '@domain-group/suit-theme'

import type {
  ProviderModel,
  PublicationModel,
} from '@models/types'

import useAssignEntityPermissionsModal from '@hooks/useAssignEntityPermissionsModal'

import NoResults from '../NoResults'

type PayloadMappingKey = 'publicationIds' | 'publicationProductIds'

type PayloadMapping = {
  [key in PayloadMappingKey]: {
    entities: string,
    filteredEntities: string,
    loadEntityIds: string,
    payloadKey: string,
    requestOptions?: {
      [key: string]: boolean,
    },
  }
}

const payloadMapping: PayloadMapping = {
  publicationIds: {
    entities: 'publications',
    filteredEntities: 'filteredPublications',
    loadEntityIds: 'loadPublicationIds',
    payloadKey: 'publications',
  },
  publicationProductIds: {
    entities: 'publicationProducts',
    filteredEntities: 'filteredPublicationProducts',
    loadEntityIds: 'loadPublicationProductIds',
    payloadKey: 'publicationProducts',
  },
}

type EntityListItemProps = {
  callbacks?: {
    onClick: () => void,
  },
  entity: ProviderModel | PublicationModel,
  selected: boolean,
}

interface EntityListProps {
  assignLayoutTemplatePermissionsPayload: ReturnType<typeof useAssignEntityPermissionsModal>,
  entityListItemComponent: (props: EntityListItemProps) => React.ReactNode,
  entityKey: PayloadMappingKey,
}

const EntityList = (props: EntityListProps) => {
  const {
    assignLayoutTemplatePermissionsPayload: {
      callbacks: {
        bulkDeselectEntityIds,
        bulkSelectEntityIds,
        filterUnassignedEntities,
        filterVisibleEntities,
        setCurrentSegmentKey,
        toggleEntityId,
      },
      permissions: {
        queued: queuedPermissions,
      },
      payloads,
      payloads: { groupedPublicationProducts },
    },
    entityKey,
    entityListItemComponent,
  } = props

  // Payload Mapping
  const payloadKeys = payloadMapping[entityKey]

  // Entities Payload
  // @ts-expect-error TS7053
  const entitiesPayload = payloads[payloadKeys.payloadKey] || {}
  const {
    callbacks: entitiesPayloadCallbacks,
    loading,
    reduxActionPerformed,
  } = entitiesPayload

  const loadEntityIdsFn = entitiesPayloadCallbacks[payloadKeys?.loadEntityIds]
  const filteredEntities = entitiesPayload[payloadKeys?.filteredEntities]
  const entityObjects = entitiesPayload[payloadKeys?.entities]
  const queuedPermissionIds = queuedPermissions[entityKey]

  // Visible & Unassigned
  const visibleEntities = filterVisibleEntities({
    entityKey,
    entities: filteredEntities,
  })

  // @ts-expect-error TS2339
  const visibleEntityIds = visibleEntities.map(p => p.id)
  const hasVisibleEntities = !!visibleEntities.length

  const unassignedEntities = filterUnassignedEntities({
    entityKey,
    entities: entityObjects,
  })

  // Load Unassigned
  useEffect(() => {
    if (reduxActionPerformed && unassignedEntities.length && loadEntityIdsFn){
      const loadEntityIdsParams = {
        // @ts-expect-error TS7006
        [entityKey]: unassignedEntities.map(p => p.id),
        entityOptions: payloadKeys.requestOptions ?? {},
      }

      loadEntityIdsFn(loadEntityIdsParams)
    }
  }, [reduxActionPerformed, unassignedEntities.length])

  const ListItemComponent = entityListItemComponent

  return (
    <>
      <LoadingModule boxProps={{ marginBottom: 0, marginTop: 'medium' }} loading={loading} />

      {!loading && !hasVisibleEntities && <NoResults callbacks={{ reset: () => setCurrentSegmentKey('all') }} />}

      {!loading && hasVisibleEntities && (
      <>
        <Box flexDirection="column" marginTop="medium" variant="white">
          {(entityKey === 'publicationIds') && visibleEntities.map(entity => (
            // @ts-expect-error TS2786
            <ListItemComponent
              // @ts-expect-error TS2339
              callbacks={{ onClick: () => toggleEntityId({ entityKey, entityId: entity.id }) }}
              // @ts-expect-error TS2339
              key={entity.id}
              entity={entity}
              // @ts-expect-error TS2345
              selected={queuedPermissionIds?.includes(entity.id)}
            />
          ))}

          {(entityKey === 'publicationProductIds') && Object.values(groupedPublicationProducts).map((entity) => {
            // @ts-expect-error TS2339
            const { title: entityTitle, publicationProducts } = entity

            // @ts-expect-error TS7006
            const hasVisibleItems = publicationProducts.some(publicationProduct => visibleEntityIds.includes(publicationProduct?.id))

            if (!hasVisibleItems) return null

            return (
              <Box flexDirection="column">
                <Text color="bodyFontLightColor" letterSpacing="0.5px" padding="large" fontSize="small">{entityTitle}</Text>

                {Object.values(publicationProducts).map((publicationProduct) => {
                  // @ts-expect-error TS2339
                  if (!visibleEntityIds.includes(publicationProduct?.id)){
                    return null
                  }

                  return (
                    // @ts-expect-error TS2786
                    <ListItemComponent
                      // @ts-expect-error TS2339
                      onClick={() => toggleEntityId({ entityKey, entityId: publicationProduct?.id })}
                      // @ts-expect-error TS18046
                      key={publicationProduct.id}
                      // @ts-expect-error TS2322
                      entity={publicationProduct}
                      // @ts-expect-error TS2345
                      selected={queuedPermissionIds?.includes(publicationProduct?.id)}
                    />
                  )
                })}
              </Box>
            )
          })}
        </Box>

        <Box marginLeft="auto" marginTop="medium" width="auto">
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
            onClick={() => bulkSelectEntityIds({
              entityKey,
              entityIds: visibleEntityIds,
            })}
            size="medium"
            width="auto"
          >
            Select Visible
          </Button>

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTimesCircle} />}
            onClick={() => bulkDeselectEntityIds({
              entityKey,
              entityIds: visibleEntityIds,
            })}
            marginLeft="medium"
            size="medium"
            width="auto"
          >
            Deselect Visible
          </Button>
        </Box>
      </>
      )}

      {!!unassignedEntities.length && (
        <FormField label="To Be Assigned" marginTop="large">
          <Box flexDirection="column" variant="white">
            {/*
             // @ts-expect-error TS7006 */}
            {unassignedEntities.map(entity => (
              // @ts-expect-error TS2786
              <ListItemComponent key={entity.id} entity={entity} selected={false} />
            ))}
          </Box>
        </FormField>
      )}
    </>
  )
}

export default EntityList
